import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { sub } from 'date-fns';

function Navbar() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(null);
    const [navbarScrolled, setNavbarScrolled] = useState(false);
    const [navbarVisible, setNavbarVisible] = useState(true);
    const lastNavbarScrollY = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
    const location = useLocation();
    const [isSubSubOpen, setIsSubSubOpen] = useState(null);

    useEffect(() => {
        let ticking = false;

        const updateNavbarVisibility = () => {
            const currentY = window.scrollY;
            const delta = currentY - lastNavbarScrollY.current;

            setNavbarScrolled(currentY > 24);

            if (currentY < 96) {
                setNavbarVisible(true);
            } else if (delta > 6) {
                setNavbarVisible(false);
            } else if (delta < -6) {
                setNavbarVisible(true);
            }

            lastNavbarScrollY.current = currentY;
            ticking = false;
        };

        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(updateNavbarVisibility);
                ticking = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (location.hash) {
            const element = document.querySelector(location.hash);
            if (element) {
                const headerHeight = document.querySelector('.page-header')?.offsetHeight || 0;
                const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementPosition - headerHeight - 80;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth',
                });
            }
        }
    }, [location]);

    const navItems = [
        { name: 'Beranda', path: '/' },
        {
            name: 'Perusahaan',
            dropdown: [
                {
                    name: 'Tentang Perusahaan', path: '/tentang-perusahaan',
                    Subdropdown: [
                        { name: 'Profil Perusahaan', path: '/tentang-perusahaan#profil-perusahaan' },
                        { name: 'Visi & Misi', path: '/tentang-perusahaan#visi-misi' },
                        { name: 'Sejarah Perusahaan', path: '/tentang-perusahaan#sejarah-perusahaan' },
                    ],
                },
                {
                    name: 'Manajemen', path: '/manajemen',
                    Subdropdown: [
                        { name: 'Dewan Komisaris', path: '/manajemen#dewan-komisaris' },
                        { name: 'Direksi', path: '/manajemen#direksi' },
                        { name: 'Struktur Organisasi', path: '/manajemen#struktur-organisasi' },
                    ],
                },
                {
                    name: 'Tata Kelola Perusahaan', path: '/tata-kelola-perusahaan/pedoman-struktur-gcg',
                    Subdropdown: [
                        { name: 'Pedoman & Struktur GCG', path: '/tata-kelola-perusahaan/pedoman-struktur-gcg' },
                        { name: 'Implementasi GCG', path: '/tata-kelola-perusahaan/implementasi-gcg' },
                        { name: 'Board Manual', path: '/tata-kelola-perusahaan/board-manual' },
                        { name: 'Kode Etik & Tata Perilaku', path: '/tata-kelola-perusahaan/kode-etik-tata-perilaku' },
                        { name: 'Gratifikasi & Benturan Kepentingan', path: '/tata-kelola-perusahaan/gratifikasi-benturan-kepentingan' },
                        { name: 'Sistem Pengaduan Pelanggaran', path: '/tata-kelola-perusahaan/sistem-pengaduan-pelanggaran' },
                        { name: 'Piagam/Charter', path: '/tata-kelola-perusahaan/piagam-charter' },
                        { name: 'Komite Perusahaan', path: '/tata-kelola-perusahaan/komite-perusahaan' },
                        { name: 'Sistem Manajemen', path: '/tata-kelola-perusahaan/sistem-manajemen' },
                    ],
                },
            ],
        },
        { name: 'Pengadaan', path: '/pengadaan' },
        {
            name: 'Palapa Ring',
            dropdown: [
                { name: 'Proyek Strategis Nasional', path: '/palapa-ring#proyek-strategis-nasional' },
                { name: 'Palapa Ring Tengah', path: '/palapa-ring#palapa-ring-tengah' },
                { name: 'Lokasi dan Wilayah', path: '/palapa-ring#lokasi-dan-wilayah' },
            ],
        },
        {
            name: 'Media',
            dropdown: [
                { name: 'Berita', path: '/media/berita' },
                { name: 'Artikel', path: '/media/artikel' },
                {
                    name: 'Publikasi', path: '/media/publikasi',
                    Subdropdown: [
                        { name: 'Annual Report', path: '/media/publikasi/annual-report' },
                    ],
                },
            ],
        },
        { name: 'Kontak Kami', path: '/kontak-kami' },
    ];

    return (
        <>
            <nav
                className={`fixed top-0 w-full z-[100] px-5 h-20 transform-gpu will-change-transform transition-[transform,background-color] duration-300 ease-out ${(isMenuOpen || isDropdownOpen !== null || navbarVisible) ? 'translate-y-0' : '-translate-y-full'} ${navbarScrolled ? 'bg-gradient-to-b from-black to-[#111827] text-white' : 'bg-gradient-to-b from-black to-[#111827] text-white'
                    }`}
            >
                <div className="mx-auto flex items-center justify-between h-full px-2">
                    <div className="flex items-center">
                        <Link to="/" className="hover:text-gray-300">
                            <LazyLoadImage alt="LOGO LTI" className="h-12" src={`${process.env.PUBLIC_URL}/assets-web/logo_lti.png`} effect="blur" />
                        </Link>
                    </div>

                    <div className="lg:hidden">
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="text-white hover:text-gray-300 focus:outline-none"
                        >
                            {isMenuOpen ? (
                                <i className="fa-solid fa-xmark w-6 h-6"></i> // Close icon
                            ) : (
                                <i className="fa-solid fa-bars w-6 h-6"></i> // Menu (bars) icon
                            )}
                        </button>
                    </div>

                    {/* Menu Desktop */}
                    <ul className="hidden lg:flex space-x-6">
                        {navItems.map((item, index) => {
                            // tentukan parentPath: kalau dropdown ambil path sebelum hash (#)
                            const parentPath = item.dropdown ? item.dropdown[0].path.split('#')[0] : item.path;
                            const isActive = location.pathname === parentPath;

                            return (
                                <li
                                    key={index}
                                    className="relative shadow-xl shadow-inner"
                                    onMouseEnter={() => setIsDropdownOpen(item.name)}
                                    onMouseLeave={() => setIsDropdownOpen(null)}
                                >
                                    {!item.dropdown ? (
                                        <Link
                                            to={item.path}
                                            className={`hover:text-blue-500 text-sm ${isActive ? 'text-yellow-500 font-semibold' : ''}`}
                                        >
                                            {item.name}
                                        </Link>
                                    ) : (
                                        <>
                                            <span
                                                className={`hover:text-blue-500 text-sm cursor-pointer ${isDropdownOpen === item.name || isActive ? 'text-yellow-500 font-semibold' : ''
                                                    }`}
                                            >
                                                {item.name}
                                                <i className="fa-solid fa-caret-down ml-1"></i>
                                            </span>
                                            {isDropdownOpen === item.name && (
                                                <div className={`sub-dropdown-nav ${item.name === 'Media' ? 'dropdown-edge-right' : ''} absolute left-0 top-full mt-0 bg-white text-black shadow-2xl shadow-inner rounded-2xl z-[110]`}>
                                                    <ul className="py-2 whitespace-nowrap">
                                                        {item.dropdown
                                                            .filter(dropdownItem => dropdownItem.path !== "#")
                                                            .map((dropdownItem, idx) => (
                                                                <li key={idx} className="group/sub relative hover:bg-gray-200 hover:text-blue-500 transition-colors duration-300 px-4 py-2 rounded-md">
                                                                    <div className="flex items-center justify-between w-full">
                                                                        <Link to={dropdownItem.path} className="block px-4 py-2 text-sm flex-1">
                                                                            {dropdownItem.name}
                                                                        </Link>

                                                                        {/* Tampilkan panah jika ada Subdropdown */}
                                                                        {dropdownItem.Subdropdown && (
                                                                            <i className={`fa-solid ${item.name === 'Media' ? 'fa-caret-left' : 'fa-caret-right'} ml-2 text-xs text-gray-400 group-hover/sub:text-blue-500`}></i>
                                                                        )}
                                                                    </div>

                                                                    {/* Kontainer Sub-Submenu yang muncul ke samping kanan */}
                                                                    {dropdownItem.Subdropdown && (
                                                                        <div className={`absolute ${item.name === 'Media' ? 'submenu-left' : ''} left-full top-0 ml-0 hidden group-hover/sub:block bg-white text-black shadow-2xl rounded-2xl min-w-[200px] z-[120]`}>
                                                                            <ul className="py-2 whitespace-nowrap">
                                                                                {dropdownItem.Subdropdown
                                                                                    .filter(subItem => subItem.path !== "#")
                                                                                    .map((subItem, subIdx) => (
                                                                                        <li key={subIdx} className="hover:bg-gray-200 hover:text-blue-500 transition-colors duration-300 px-4 py-2 rounded-md">
                                                                                            <Link to={subItem.path} className="block px-4 py-2 text-sm">
                                                                                                {subItem.name}
                                                                                            </Link>
                                                                                        </li>
                                                                                    ))}
                                                                            </ul>
                                                                        </div>
                                                                    )}
                                                                </li>
                                                            ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>

                {/* Menu Mobile */}
                {isMenuOpen && (
                    <div className={`fixed top-20 right-0 w-full bg-gradient-to-b from-[#111827] to-black text-white min-h-screen z-[110] overflow-y-auto transition-transform duration-500`}>
                        <ul className="flex flex-col items-start space-y-4 p-8">
                            {navItems.map((item, index) => (
                                <li key={index} className="w-full">
                                    {!item.dropdown ? (
                                        <Link to={item.path} className="text-white text-base block pb-2" onClick={() => setIsMenuOpen(false)}>
                                            {item.name}
                                        </Link>
                                    ) : (
                                        <>
                                            {/* Level 1: Menu Utama (Contoh: Perusahaan) */}
                                            <div
                                                onClick={() => setIsDropdownOpen(isDropdownOpen === item.name ? null : item.name)}
                                                className="text-white text-base cursor-pointer flex items-center justify-between w-full pb-2"
                                            >
                                                {item.name}
                                                <i className={`fa-solid fa-caret-down transition-transform ${isDropdownOpen === item.name ? 'rotate-180' : ''}`}></i>
                                            </div>

                                            {isDropdownOpen === item.name && (
                                                <ul className="mt-2 ml-4 space-y-3">
                                                    {item.dropdown
                                                        .filter(dropdownItem => dropdownItem.path !== "#")
                                                        .map((dropdownItem, idx) => (
                                                            <li key={idx} className="w-full">
                                                                {/* Level 2: Sub-menu (Contoh: Tentang Perusahaan) */}
                                                                {dropdownItem.Subdropdown ? (
                                                                    <>
                                                                        <div
                                                                            onClick={() => setIsSubSubOpen(isSubSubOpen === dropdownItem.name ? null : dropdownItem.name)}
                                                                            className="text-gray-300 text-sm cursor-pointer flex items-center justify-between w-full py-1 pr-4"
                                                                        >
                                                                            {dropdownItem.name}
                                                                            {/* Menggunakan fa-caret-down agar sama dengan Perusahaan, tapi ukuran lebih kecil */}
                                                                            <i className={`fa-solid fa-caret-down text-[10px] transition-transform duration-300 ${isSubSubOpen === dropdownItem.name ? 'rotate-180' : ''}`}></i>
                                                                        </div>

                                                                        {/* Level 3: Sub-Sub-menu (Contoh: Profil Perusahaan) */}
                                                                        {isSubSubOpen === dropdownItem.name && (
                                                                            <ul className="ml-4 mt-2 space-y-2">
                                                                                {dropdownItem.Subdropdown
                                                                                    .filter(subItem => subItem.path !== "#")
                                                                                    .map((subItem, subIdx) => (
                                                                                        <li key={subIdx}>
                                                                                            <Link
                                                                                                to={subItem.path}
                                                                                                className="text-gray-400 text-[11px] block py-1"
                                                                                                onClick={() => setIsMenuOpen(false)}
                                                                                            >
                                                                                                {subItem.name}
                                                                                            </Link>
                                                                                        </li>
                                                                                    ))}
                                                                            </ul>
                                                                        )}
                                                                    </>
                                                                ) : (

                                                                    <Link
                                                                        to={dropdownItem.path}
                                                                        className="text-gray-300 text-sm block py-1"
                                                                        onClick={() => setIsMenuOpen(false)}
                                                                    >
                                                                        {dropdownItem.name}
                                                                    </Link>
                                                                )}
                                                            </li>
                                                        ))}
                                                </ul>
                                            )}
                                        </>
                                    )}
                                </li>
                            ))}
                        </ul>

                        {/* Social Media Section */}
                        <div className='text-center'>
                            <h5 className="text-sm sm:text-md font-bold mb-3 sm:mb-4 border-b-2 border-white inline-block">
                                Social Media
                            </h5>
                            <div className="flex justify-center space-x-3 sm:space-x-4">
                                <a href="https://www.facebook.com/len.telko/" target="_blank" rel="noopener noreferrer">
                                    <i className="fab fa-facebook-f text-base sm:text-lg hover:text-gray-300 transition"></i>
                                </a>
                                <a href="https://www.instagram.com/len.telko/?hl=en" target="_blank" rel="noopener noreferrer">
                                    <i className="fab fa-instagram text-base sm:text-lg hover:text-gray-300 transition"></i>
                                </a>
                                <a href="https://www.linkedin.com/company/len-telko/" target="_blank" rel="noopener noreferrer">
                                    <i className="fab fa-linkedin-in text-base sm:text-lg hover:text-gray-300 transition"></i>
                                </a>
                            </div>
                        </div>

                        {/* Copyright Section */}
                        <div className="border-t border-white mt-6 sm:mt-8 pt-3 sm:pt-4 text-center text-xs sm:text-sm">
                            <span>Copyright &copy; 2024 PT Len Telekomunikasi Indonesia. <br />All Rights Reserved.</span>
                        </div>

                        {/* Logo di bawah */}
                        {/* <div className="absolute bottom-30 left-1/2 transform -translate-x-1/2">
                            <LazyLoadImage alt="LOGO LTI" className="h-12" src={`${process.env.PUBLIC_URL}/assets-web/logo_lti.png`} effect="blur" />
                        </div> */}
                    </div>
                )}
            </nav>
        </>
    );
}

export default Navbar;
